import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import {
  ThemeProvider,
  Box,
  Toolbar,
  CircularProgress,
  Typography,
  Button,
} from "@mui/material";
import Header from "../Components/header";
import Footer from "../Components/footer";
import EditProjectForm from "../Components/editProject";
import ErrorFallback from "../Components/errorFallback";
import { useAuth } from "../Context/authContext";
import { useNavigationBlocker } from "../Context/navigationBlockerContext";
import theme from "../Styles/theme";
import "../Styles/general.css";
import {
  fetchAllProjectsOfManagerAndUnit,
  fetchProjectForAutofill,
  saveFiles,
  fetchProject,
  saveProject,
  submitProject,
  getTimeEditTermCategories,
} from "../Controllers/projectController";
import { fetchUnits } from "../Controllers/unitController";
import { fetchAllCategories } from "../Controllers/categoryController";
import { fetchEmployees } from "../Controllers/employeeController";
import { fetchExpensesConfirmers } from "../Controllers/expensesConfirmerController";
import { useError } from "../Context/errorContext";
import { rowType, statuses } from "../Components/helpers";

const RegisterProject = () => {
  const navigate = useNavigate();
  const { enableBlock, disableBlock, isBlocking } = useNavigationBlocker();
  const { user } = useAuth();
  const { uid } = useParams();

  const [form, setForm] = useState({
    project_manager_name: user?.userData?.name || null,
    project_manager_email: user?.userData?.userName || null,
    unit_id: user?.userData?.divisionUnits?.[0] || null,
    project_id: null,
    requires_progress_reporting: false,
    initial_progress: 0,
  });
  const [members, setMembers] = useState([]);
  const [expensesConfirmers, setExpensesConfirmers] = useState([]);
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [resultMessage, setResultMessage] = useState(null);
  const [isDraft, setIsDraft] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [budget, setBudget] = useState([
    {
      name: "Budget Approved By Funder",
      value_display: "0,00",
      value: 0,
      row_type: rowType.approvedBudget,
    },
    {
      name: "EBS Own Contribution",
      value_display: "0,00",
      value: 0,
      row_type: rowType.selfContribution,
    },
    {
      name: "Total",
      value_display: "0,00",
      value: 0,
      row_type: rowType.total,
    },
    {
      name: "Internal Staff Cost",
      value: 0,
      value_display: "0,00",
      row_type: rowType.staffCost,
    },
    {
      name: "EBS Revenue",
      value: 0,
      value_display: "0,00",
      row_type: rowType.revenue,
    },
  ]);
  const [revenueSharing, setRevenueSharing] = useState([]);
  const [uploads, setUploads] = useState([]);

  const [loading, setLoading] = useState(true);
  const [managerProjects, setManagerProjects] = useState([]);
  const { error, setError, setAlert } = useError();

  const [saving, setSaving] = useState(false);
  const [timeEditTermCategories, setTimeEditTermCategories] = useState([]);

  const isManager =
    user.userData.userRoles.includes("Unit Manager") &&
    (user.userData.managedUnits.includes(form?.unit_id) ||
      form.approvers?.find((a) => a.approver_email === user.userData.userName));
  const isAccountant = user.userData.userRoles.includes("Accounting");
  const isAdmin = user.userData.userRoles.includes("Administrator");

  const fetchAllData = async () => {
    try {
      const res1 = fetchUnits(setUnits);
      const res2 = getTimeEditTermCategories(
        setTimeEditTermCategories,
        setAlert,
      );
      const res3 = fetchEmployees(setEmployees);
      const res4 = fetchAllProjectsOfManagerAndUnit(
        form.project_manager_email,
        form.unit_id,
        setManagerProjects,
      );
      const res5 = fetchExpensesConfirmers(setExpensesConfirmers);
      let res6;
      if (uid) {
        res6 = fetchProject(
          uid,
          setForm,
          setBudget,
          setRevenueSharing,
          setCategories,
          setMembers,
          setUploads,
          setError,
        );
      }
      await Promise.all([res1, res2, res3, res4, res5, res6]);
    } finally {
      setLoading(false);
    }
  };

  const autofillProjectForm = async (uid) => {
    await fetchProjectForAutofill(
      uid,
      setForm,
      setBudget,
      setRevenueSharing,
      setCategories,
      setMembers,
      setError,
    );
  };

  useEffect(() => {
    fetchAllData();
  }, [user, uid]);

  // Handle browser reload/tab close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isBlocking()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isBlocking]);

  // Handle form field changes
  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    const prevForm = { ...form };
    if (id === "unit_id") {
      fetchAllProjectsOfManagerAndUnit(
        form.project_manager_email,
        value,
        setManagerProjects,
      );
    }
    if (id === "activity_id" || id === "unit_id") {
      const activity_id = id === "activity_id" ? value : form.activity_id;
      const unit_id = id === "unit_id" ? value : form.unit_id;
      fetchAllCategories(unit_id, activity_id, setCategories);
      prevForm.subactivities = [];
    }
    if (id === "expenses_confirmed") {
      prevForm.expenses_confirmed_by_email = value.email;
      prevForm.expenses_confirmed_by_name = value.name;
    } else {
      prevForm[id] = type === "checkbox" ? checked : value;
    }
    setForm(prevForm);
    enableBlock();
  };

  const handleSubmit = async (e, isDraft) => {
    if (e) e.preventDefault();
    const projectData = {
      ...form,
      members,
      budget,
      revenue_sharing: JSON.stringify(
        revenueSharing.map((row) => ({
          unit: row.unit,
          percent: row.percent,
          euro: row.euro,
        })),
      ),
      isSubmit: true,
      isDraft,
    };
    const project = await submitProject(
      projectData,
      setResultMessage,
      disableBlock,
      navigate,
    );
    if (uploads.length !== 0) {
      saveFiles(project.uid, uploads, setAlert);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const projectData = {
      uid,
      ...form,
      members,
      budget,
      revenue_sharing: JSON.stringify(
        revenueSharing.map((row) => ({
          unit: row.unit,
          percent: row.percent,
          euro: row.euro,
        })),
      ),
      isSubmit: false,
    };
    const project = await saveProject(
      projectData,
      setResultMessage,
      disableBlock,
      navigate,
    );
    if (uploads.length !== 0) {
      saveFiles(project.uid, uploads, setAlert);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Box className="mainContainer" textAlign="center">
        <CircularProgress />
        <Typography>Loading project...</Typography>
      </Box>
    );
  }
  if (error) {
    return null;
  }
  // In case project exists, check if the user is not allowed to edit the project
  // (is not Admin and status is waiting for Unit Manager/Accounting review or post Confirmed)
  if (
    uid &&
    !(
      (user?.userData?.userName === form.project_manager_email &&
        (form.status_id === statuses.DRAFT ||
          form.status_id === statuses.PREPARING_FOR_SIGNOFF)) ||
      isAccountant ||
      isAdmin ||
      isManager
    )
  ) {
    return (
      <Box className="mainContainer" textAlign="center">
        <Typography color="error">Unauthorized access to project</Typography>
        <Button
          onClick={() => navigate("/")}
          variant="contained"
          color="primary"
        >
          Go Back to Projects
        </Button>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <Toolbar sx={{ height: "120px" }} />
      <Box className="mainContainer">
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <div>
            <EditProjectForm
              form={form}
              units={units}
              categories={categories}
              resultMessage={resultMessage}
              handleInputChange={handleInputChange}
              handleSubmit={handleSubmit}
              handleSave={handleSave}
              isAccountant={isAccountant}
              employees={employees}
              members={members}
              setMembers={setMembers}
              expensesConfirmers={expensesConfirmers}
              isDraft={isDraft}
              setIsDraft={setIsDraft}
              confirmDialogOpen={confirmDialogOpen}
              setConfirmDialogOpen={setConfirmDialogOpen}
              confirmation={confirmation}
              setConfirmation={setConfirmation}
              user={user}
              budget={budget}
              setBudget={setBudget}
              revenueSharing={revenueSharing}
              setRevenueSharing={setRevenueSharing}
              managerProjects={managerProjects}
              autofillProjectForm={autofillProjectForm}
              uploads={uploads}
              setUploads={setUploads}
              saving={saving}
              timeEditTermCategories={timeEditTermCategories}
            />
          </div>
        </ErrorBoundary>
      </Box>
      <Footer />
    </ThemeProvider>
  );
};

export default RegisterProject;
